// Refactored code from spinbox 1 - computeUtils
import { transform } from 'ol/proj';
import GeomLineString from 'ol/geom/LineString';
import { getDistance, getArea } from 'ol/sphere';
import { roundNumber } from './roundNumber';
import { getCommonRotateData } from './style/rotateText';
export function calculateDistance(point1, point2, projection) {
    const line = new GeomLineString([point1, point2]);
    return projection === 'EPSG:5514'
        ? line.getLength()
        : calculateHaversineDistance(line, projection);
}
// ------------------
/**
 * Get distance by haversineDistance
 */
export function calculateHaversineDistance(line, projection) {
    let length = 0;
    const coordinates = line.getCoordinates();
    const c1 = transform(coordinates[0], projection, 'EPSG:4326');
    const c2 = transform(coordinates[1], projection, 'EPSG:4326');
    length = getDistance(c1, c2);
    return length;
}
export function getValueWithUnits(value) {
    const units = value > 1000 ? 'km' : 'm';
    const convertedValue = units === 'km' ? value / 1000 : value;
    return { value: convertedValue, units };
}
export function roundNumberByUnitsLength(units, value) {
    const constTrans = units === 'm' ? 1000 : 1;
    let roundedValue;
    // 1km - 10km
    if (value > constTrans && value < constTrans * 10) {
        roundedValue = roundNumber(value, 3);
        // 10km - 100km
    }
    else if (value > constTrans * 10 && value < constTrans * 100) {
        roundedValue = roundNumber(value, 2);
        // 100km - 1000km
    }
    else if (value > constTrans * 100 && value < constTrans * 1000) {
        roundedValue = roundNumber(value, 1);
        // > 1000km
    }
    else if (value > constTrans * 1000) {
        roundedValue = roundNumber(value, 0);
        // 1m - 1000m
    }
    else {
        roundedValue = roundNumber(value, 1);
    }
    return roundedValue;
}
export function distanceBetweenPoints(point1, point2, projection) {
    // convert m/km
    const convertedLength = getValueWithUnits(calculateDistance(point1, point2, projection));
    const length = roundNumberByUnitsLength(convertedLength.units, convertedLength.value);
    return {
        index: 0,
        value: length,
        units: convertedLength.units,
        cumulativeSum: '',
        cumulativeSumUnits: convertedLength.units,
    };
}
export function calculateArea(polygon, projection) {
    if (polygon.getType() !== 'Polygon') {
        return;
    }
    let areaData = 0;
    let units = '';
    let area;
    let areaFormat = '';
    let decimalPlaces = 1;
    areaData =
        projection === 'EPSG:5514' ? polygon.getArea() : calculateGeodesicArea(polygon, projection);
    // convert by units (m2, ha, km2)
    // m2
    if (areaData < 100000) {
        area = areaData;
        // units = 'm&sup2;';
        units = `m${String.fromCharCode(178)}`;
        decimalPlaces = 1;
    }
    else if (areaData >= 100000 && areaData < 1000000) {
        // ha
        area = areaData / 10000;
        units = 'ha';
        decimalPlaces = 1;
    }
    else if (areaData >= 1000000) {
        // km2
        area = areaData / 1000000;
        // units = 'km&sup2;';
        units = `km${String.fromCharCode(178)}`;
        if (area < 100) {
            decimalPlaces = 2;
        }
        else if (area > 100 && area < 1000) {
            decimalPlaces = 1;
        }
        else if (area > 1000) {
            decimalPlaces = 0;
        }
    }
    // area = roundNumber(area, decimalPlaces);
    area = roundNumber(area, decimalPlaces);
    areaFormat = formatNumber(area);
    return { value: area, valueFormat: areaFormat, units };
}
/**
 * Get area by geodesicArea
 * @param {GeomPolygon} polygon
 * @return {number}
 */
export function calculateGeodesicArea(polygon, projection) {
    let area = 0;
    /** @type {GeomPolygon} */
    const geom = polygon.clone().transform(projection, 'EPSG:4326');
    area = Math.abs(getArea(geom, { projection: 'EPSG:4326' }));
    return area;
}
export function formatNumber(value = 0) {
    return value.toLocaleString();
}
// refactor !!!
export function createCumulativeSum(lengthSegment, nextLength) {
    let units = 'km';
    if (!lengthSegment) {
        return { value: nextLength.value, units: nextLength.units };
    }
    // convert m > km
    const cumulativeValue = lengthSegment.cumulativeSumUnits === units
        ? Number(lengthSegment.cumulativeSum)
        : Number(lengthSegment.cumulativeSum) / 1000;
    // convert m > km
    const length = nextLength.units === units ? Number(nextLength.value) : Number(nextLength.value) / 1000;
    let cumulativeSum = cumulativeValue + length;
    units = cumulativeSum < 1 ? 'm' : units;
    cumulativeSum = cumulativeSum < 1 ? cumulativeSum * 1000 : cumulativeSum;
    cumulativeSum = roundNumberByUnitsLength(units, cumulativeSum);
    return { value: cumulativeSum, units };
}
export function createSegments(coordinates, mapProjection) {
    const lengthData = [];
    let counter = 0;
    let length;
    const blankFirstPoint = {
        value: null,
        units: 'm',
        cumulativeSum: 0,
        cumulativeSumUnits: 'm',
        cumulativeSumFormat: '0',
        valueFormat: '0',
        rotation: 0,
        point: null,
    };
    while (counter + 1 < coordinates.length) {
        length = distanceBetweenPoints(coordinates[counter], coordinates[counter + 1], mapProjection);
        const cumulativeResult = createCumulativeSum(lengthData[counter - 1], length);
        const { rotation, coordinate } = getCommonRotateData(coordinates[counter], coordinates[counter + 1]);
        length.cumulativeSum = cumulativeResult.value;
        length.cumulativeSumUnits = cumulativeResult.units;
        length.cumulativeSumFormat = formatNumber(length.cumulativeSum);
        length.valueFormat = formatNumber(length.value);
        length.rotation = rotation;
        length.point = coordinate;
        lengthData.push(length);
        counter++;
    }
    // add units to a first(blank) point
    if (lengthData.length > 0) {
        blankFirstPoint.units = lengthData[0]?.units === 'm' ? 'm' : 'km';
        blankFirstPoint.cumulativeSumUnits = blankFirstPoint.units;
        blankFirstPoint.rotation = lengthData[0]?.rotation;
        blankFirstPoint.point = coordinates[0];
    }
    if (coordinates.length > 0) {
        // add simulate 'first' point
        lengthData.unshift(blankFirstPoint);
    }
    return lengthData;
}
export function calculateSumSegments(segments) {
    if (!segments || typeof segments !== 'object') {
        return;
    }
    let sumUnits = 'km';
    let sumFormat = '';
    let sum = segments.reduce((acc, item) => {
        if (item.value > 0) {
            acc += item.units === sumUnits ? Number(item.value) : Number(item.value) / 1000;
        }
        return acc;
    }, 0);
    sumUnits = sum < 1 ? 'm' : sumUnits;
    sum = sum < 1 ? sum * 1000 : sum;
    const roundedSum = roundNumberByUnitsLength(sumUnits, sum);
    sumFormat = formatNumber(roundedSum);
    return {
        value: sum,
        valueFormat: sumFormat,
        units: sumUnits,
    };
}
//# sourceMappingURL=measure.js.map